/* eslint-disable max-lines */
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ERuntimeSettings } from '../../common/types/runtimeSettings';
import { useApplicationContext } from '../../common/utils/applicationContext';
import { getDownPaymentFilterValue } from '../../mortgageMiniCalculator/utils';
import { selectDisplayAllFilters } from '../selectors';
import { EElementCategory, EFieldType, EStaticType, IScheme } from '../types/calculator';
import {
  EBenefitProgram,
  EEmploymentType,
  EIncomeConfirmation,
  EObjectType,
  ERealEstateType,
} from '../types/calculator/enums';
import {
  calculatePercentage,
  calculateValueFromPercentage,
  isApartments,
  isFlatOrUnselected,
  isHouseBuilding,
  isNewBuilding,
  isSecondary,
} from '../utils';
import { useComponentLayout } from './useComponentLayout';

/**
 * Хук для получения схемы формы
 */
export const useGetFormScheme = (): IScheme => {
  const { config } = useApplicationContext();
  const layout = useComponentLayout();
  const isAllFiltersVisible = useSelector(selectDisplayAllFilters);

  const downPaymentOptions = config.getStrict<number[]>(ERuntimeSettings.downPaymentOptions);
  const minDownPayment = config.getStrict<number>(ERuntimeSettings.minDownPayment);

  return useMemo(
    () =>
      ({
        elements: [
          {
            category: EElementCategory.Group,
            elements: [
              {
                category: EElementCategory.Field,
                label: 'Регион покупки',
                type: EFieldType.Suggest,
                name: 'regionId',
                placeholder: 'Укажите регион',
                // обогащается после получения пропсов
                source: [{ label: 'Москва', value: '1' }],
              },
              {
                category: EElementCategory.Field,
                label: 'Стоимость недвижимости',
                placeholder: '12 000 000',
                type: EFieldType.InputNumber,
                decimalDigits: 0,
                name: 'objectCost',
                rightAdornment: '₽',
                validation: {
                  validate: (value, state) => {
                    if (Number(value) <= state.downPayment) {
                      return 'Стоимость должна быть больше первого взноса';
                    }

                    return undefined;
                  },
                  min: {
                    message: 'Минимальная сумма — 1 ₽',
                    value: 2,
                  },
                  max: {
                    message: 'Максимальная сумма — 999 999 999 ₽',
                    value: 999999999,
                  },
                  required: 'Поле нужно заполнить',
                },
              },
              {
                category: EElementCategory.Field,
                label: 'Первый взнос',
                placeholder: '4 000 000',
                type: EFieldType.InputWithOptions,
                name: 'downPayment',
                decimalDigits: 0,
                rightAdornment: '₽',
                source: state => {
                  const options = downPaymentOptions.map(option => {
                    const filterValue = getDownPaymentFilterValue(option);
                    // Проверяем, что процент от текущего downPayment попадает в диапазон,
                    // например, от 20 до 20.1, чтобы включить предложения с неровными процентами
                    const currentPercentage = calculatePercentage(state.objectCost, state.downPayment);
                    // 0.001 на случай ошибки плавующей запятой
                    const isChecked = currentPercentage >= option && currentPercentage <= filterValue + 0.001;

                    return {
                      label: `${option} %`,
                      value: `${option}`,
                      checked: isChecked,
                      dataTestId: `down-payment-${option}`,
                    };
                  });

                  return layout === 'small' ? options.slice(0, 4) : options;
                },
                calculateForField: (value, state) => {
                  if (state && state.objectCost) {
                    const filterValue = getDownPaymentFilterValue(Number(value));

                    return calculateValueFromPercentage(state.objectCost, filterValue);
                  }

                  return 0;
                },
                calculateForChips: (value, state) => {
                  if (state && state.objectCost) {
                    return calculatePercentage(state.objectCost, value);
                  }

                  return 0;
                },
                validation: {
                  validate: (value, state) => {
                    if (state.objectCost === undefined) {
                      return 'Сначала необходимо указать стоимость недвижимости';
                    }
                    if (Number(value) >= state.objectCost) {
                      return 'Взнос должен быть меньше стоимости недвижимости';
                    }
                    if (Number(value) < (state.objectCost * minDownPayment) / 100) {
                      return `Минимальный взнос - ${minDownPayment}% от стоимости недвижимости`;
                    }

                    return undefined;
                  },
                  min: {
                    message: 'Минимальная сумма — 1 ₽',
                    value: 2,
                  },
                  required: 'Поле нужно заполнить',
                },
              },
              {
                category: EElementCategory.Field,
                label: 'Тип недвижимости',
                type: EFieldType.Chips,
                name: 'objectType',
                source: [
                  {
                    label: 'Квартира',
                    value: EObjectType.Flat,
                  },
                  {
                    label: 'Апартаменты',
                    value: EObjectType.Apartments,
                  },
                  {
                    label: 'Дом',
                    value: EObjectType.House,
                  },
                ],
              },
              {
                category: EElementCategory.Field,
                label: 'Тип объекта',
                type: EFieldType.Chips,
                name: 'realEstateType',
                source: state => [
                  { label: 'Новостройка', value: ERealEstateType.NewBuilding },
                  { label: 'Вторичка', value: ERealEstateType.Secondary },
                  {
                    label: 'Строительство дома',
                    value: ERealEstateType.HouseBuilding,
                    disabled: isApartments(state) || isFlatOrUnselected(state),
                  },
                ],
              },
              {
                category: EElementCategory.Field,
                label: 'Программа',
                type: EFieldType.Chips,
                offset: { bottom: 12 },
                name: 'benefitProgram',
                defaultValue: EBenefitProgram.NoProgram,
                source: state => {
                  const familyProgramDisable =
                    isSecondary(state) ||
                    isHouseBuilding(state) ||
                    (isNewBuilding(state) && !isFlatOrUnselected(state)) ||
                    (isNewBuilding(state) && isApartments(state));
                  const ItProgramDisable =
                    isSecondary(state) || isHouseBuilding(state) || (isNewBuilding(state) && isApartments(state));

                  return [
                    { label: 'ИТ-ипотека ∙ 5%', value: EBenefitProgram.It, disabled: ItProgramDisable },
                    { label: 'Семейная ∙ 6%', value: EBenefitProgram.Family, disabled: familyProgramDisable },
                    {
                      label: 'Базовая',
                      value: EBenefitProgram.NoProgram,
                    },
                  ];
                },
              },
              {
                category: EElementCategory.Field,
                label: 'Срок кредита',
                placeholder: '30 лет',
                type: EFieldType.InputWithOptions,
                decimalDigits: 0,
                name: 'loanTerm',
                rightAdornment: 'лет',
                source: [
                  { label: '15 лет', value: '15' },
                  { label: '20 лет', value: '20' },
                  { label: '30 лет', value: '30' },
                ],
                validation: {
                  min: {
                    message: 'Срок кредита не может быть меньше 1 года',
                    value: 1,
                  },
                  max: {
                    message: 'Срок кредита не может превышать 35 лет',
                    value: 35,
                  },
                  required: 'Выберите значение',
                },
              },
              {
                category: EElementCategory.Field,
                label: 'Тип занятости',
                hidden: !isAllFiltersVisible,
                type: EFieldType.Chips,
                name: 'typeOfEmployment',
                source: [
                  { label: 'Наёмный работник', value: EEmploymentType.Employee },
                  { label: 'Самозанятый', value: EEmploymentType.SelfEmployed },
                  { label: 'ИП', value: EEmploymentType.Entrepreneur },
                  { label: 'Собственник бизнеса', value: EEmploymentType.BusinessOwner },
                ],
              },
              {
                category: EElementCategory.Field,
                type: EFieldType.Radio,
                name: 'incomeConfirm',
                hidden: !isAllFiltersVisible,
                label: 'Подтверждение дохода',
                source: [
                  {
                    label: 'С подтверждением дохода',
                    value: EIncomeConfirmation.Confirmation,
                    description: 'По документам на выбор: 2-НДФЛ, 3-НДФЛ, справке по форме банка или выписке из СФР',
                  },
                  {
                    label: 'Без подтверждения дохода',
                    value: EIncomeConfirmation.NoConfirmation,
                    description: 'По двум документам: паспорт и СНИЛС',
                  },
                ],
              },
              {
                category: EElementCategory.Field,
                label: 'Зарплатная карта',
                type: EFieldType.Select,
                hidden: !isAllFiltersVisible,
                name: 'payrollBank',
                // обогащается после получения пропсов
                source: [],
                placeholder: 'Выберите банк',
                disabled: state => state.typeOfEmployment === EEmploymentType.SelfEmployed,
              },
              {
                category: EElementCategory.Static,
                label: 'allFiltersBtn',
                type: EStaticType.MoreFiltersButton,
              },
            ],
          },
        ],
      }) satisfies IScheme,
    [isAllFiltersVisible, downPaymentOptions, layout],
  );
};
